// Phase 6.0 STEP C+D · 기획상품/테마 추천
// - 관리자 편성 테마 (home_themes) 우선 표시
// - 테마 없거나 카테고리 필터 시 자동 seller_products fallback
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gift, ChevronRight, Eye } from 'lucide-react';
import api from '@/lib/api';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

const AUTO_THEMES = [
  { key: null,       label: '전체',   emoji: '✨' },
  { key: 'fashion',  label: '패션',   emoji: '👗' },
  { key: 'beauty',   label: '뷰티',   emoji: '💄' },
  { key: 'food',     label: '푸드',   emoji: '🍜' },
  { key: 'living',   label: '리빙',   emoji: '🛋️' },
];

export default function ThemedProducts() {
  const [adminThemes, setAdminThemes] = useState([]);
  const [activeTheme, setActiveTheme] = useState(null); // theme object or null
  const [fallbackItems, setFallbackItems] = useState([]);
  const [autoCat, setAutoCat] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get('/public/home/themes?limit=6')
      .then((r) => {
        const themes = r.data?.items || [];
        setAdminThemes(themes);
        if (themes.length) setActiveTheme(themes[0]);
      })
      .catch(() => setAdminThemes([]))
      .finally(() => setLoaded(true));
  }, []);

  // Auto category fallback (STEP C)
  useEffect(() => {
    if (adminThemes.length) return;
    const url = `/public/home/themed-products?limit=8${autoCat ? `&theme=${autoCat}` : ''}`;
    api.get(url).then((r) => setFallbackItems(r.data?.items || [])).catch(() => setFallbackItems([]));
  }, [adminThemes.length, autoCat]);

  if (loaded && adminThemes.length === 0 && fallbackItems.length === 0 && !autoCat) return null;

  const items = adminThemes.length ? (activeTheme?.products || []) : fallbackItems;

  return (
    <section className="py-8 sm:py-10 border-b border-black/5 dark:border-white/10" data-testid="home-themed-products">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Gift className="text-purple-500" size={22}/>
            <h2 className="text-xl sm:text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="themed-products-title">
              🎁 기획상품 · 테마 추천
            </h2>
          </div>
          {activeTheme && (
            <Link to={activeTheme.cta_link || '/schedule'} className="text-xs sm:text-sm font-bold text-purple-600 hover:underline inline-flex items-center gap-0.5" data-testid="themed-viewall">
              더 보기 <ChevronRight size={14}/>
            </Link>
          )}
        </div>

        {activeTheme?.description && (
          <p className="text-xs text-gray-500 mb-3" data-testid="themed-desc">{activeTheme.description}</p>
        )}

        {/* Theme tabs · 관리자 편성이 있으면 관리자 테마, 없으면 자동 카테고리 */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-3
          [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {adminThemes.length ? adminThemes.map((t) => (
            <button key={t.theme_id} onClick={() => setActiveTheme(t)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                activeTheme?.theme_id === t.theme_id
                  ? 'bg-purple-500 border-purple-500 text-white'
                  : 'border-black/10 dark:border-white/15 hover:bg-black/5 dark:hover:bg-white/10'
              }`}
              data-testid={`themed-chip-${t.theme_id}`}>
              {t.name}
            </button>
          )) : AUTO_THEMES.map((t) => (
            <button key={t.key || 'all'} onClick={() => setAutoCat(t.key)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                autoCat === t.key
                  ? 'bg-purple-500 border-purple-500 text-white'
                  : 'border-black/10 dark:border-white/15 hover:bg-black/5 dark:hover:bg-white/10'
              }`}
              data-testid={`themed-chip-${t.key || 'all'}`}>
              <span className="mr-1">{t.emoji}</span>{t.label}
            </button>
          ))}
        </div>

        {items.length === 0 ? (
          <p className="text-center text-xs text-gray-500 py-8">이 테마의 추천 상품이 아직 없습니다.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {items.map((p) => {
              const off = p.original_price > p.sale_price && p.original_price > 0
                ? Math.round(((p.original_price - p.sale_price) / p.original_price) * 100)
                : 0;
              return (
                <Link key={p.product_id} to={`/product/${p.product_id}`}
                  className="rounded-2xl overflow-hidden border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  data-testid={`themed-card-${p.product_id}`}>
                  <div className="relative aspect-square bg-gray-100 dark:bg-white/5 overflow-hidden">
                    {p.image && (
                      <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover"/>
                    )}
                    {off > 0 && (
                      <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-black">-{off}%</span>
                    )}
                    {p.view_count > 100 && (
                      <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-black inline-flex items-center gap-0.5">
                        <Eye size={9}/> {p.view_count.toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-[10px] text-gray-500 truncate">{p.seller_name || '판매자'}</p>
                    <p className="text-sm font-bold truncate mt-0.5">{p.name}</p>
                    <div className="flex items-baseline gap-1.5 mt-1.5">
                      <span className="text-base font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>{won(p.sale_price)}</span>
                      {off > 0 && (
                        <span className="text-[10px] line-through text-gray-400">{won(p.original_price)}</span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
